import Quill from 'quill'
import {html_element} from '@bitstillery/common/lib/dom'
import {unchecked} from '@bitstillery/common/lib/unchecked'
import {present} from '@bitstillery/common/lib/utils'

import type {BlockEmbed as QuillBlockEmbed} from 'quill/blots/block'

// Quill's registry types only a handful of its names.
const BlockEmbed = unchecked<typeof QuillBlockEmbed>(Quill.import('blots/block/embed'))

/** An image as `value()` stores it; a pasted document's image may carry `src` instead of `url`. */
interface ImageValue {
    alt?: string | null
    height?: string | null
    src?: string | null
    style?: string | null
    url?: string | null
    width?: string | null
}
/**
 * Required, so deserialized html documents with
 * images, containing inline style attributes (.e.g. image resize),
 * will still contain those attributes after loading the content.
 */
export class ImageBlot extends BlockEmbed {
    static override blotName = 'image'
    static override tagName = 'img'
    static override scope = BlockEmbed.scope

    static override create(value?: string | ImageValue | null) {
        const node = present(html_element(super.create()), 'image blot element')
        if (typeof value === 'string') {
            node.setAttribute('src', value)
        } else if (typeof value === 'object' && value) {
            node.setAttribute('src', value.url || value.src || '')
            if (value.alt) {
                node.setAttribute('alt', value.alt)
            }
            if (value.width) {
                node.setAttribute('width', value.width)
            }
            if (value.height) {
                node.setAttribute('height', value.height)
            }
            if (value.style) {
                node.setAttribute('style', value.style)
            }
        }
        return node
    }

    static override value(node: HTMLElement) {
        return {
            alt: node.getAttribute('alt'),
            height: node.getAttribute('height'),
            style: node.getAttribute('style'),
            url: node.getAttribute('src'),
            width: node.getAttribute('width'),
        }
    }
}
